import React, { useState } from 'react';

const TERMS_SECTIONS = [
  {
    title: '1. Driver Eligibility',
    content:
      'You must be at least 18 years old, hold a valid driving license for the vehicle type you intend to operate, and have a clean driving record. OnTheGo reserves the right to reject applications that do not meet our safety standards.',
  },
  {
    title: '2. Vehicle Requirements',
    content:
      'Your vehicle must be roadworthy, properly insured, and meet OnTheGo vehicle standards. Vehicles older than 10 years may be subject to additional inspection. You are responsible for maintaining your vehicle in good working condition.',
  },
  {
    title: '3. Earnings & Payments',
    content:
      'Drivers earn a percentage of each fare as specified in the current rate card. OnTheGo retains a platform fee of 15-20% depending on service type. Payments are processed weekly via M-Pesa or bank transfer.',
  },
  {
    title: '4. Code of Conduct',
    content:
      'Drivers must maintain professional conduct at all times, follow traffic laws, treat customers with respect, and complete accepted orders. Violations may result in account suspension or permanent deactivation.',
  },
  {
    title: '5. Data & Privacy',
    content:
      'By registering, you consent to OnTheGo collecting and processing your personal data as described in our Privacy Policy. Your location data is collected during active trips only and is not shared with third parties.',
  },
];

const TermsSubmitStep = ({ data, onChange, onSubmit, onBack, isSubmitting }) => {
  const [expandedSection, setExpandedSection] = useState(null);

  const allAccepted = data?.termsAccepted && data?.privacyAccepted;

  return (
    <div className="max-w-lg mx-auto">
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-2" style={{ color: '#0a1628' }}>
          Terms & Conditions
        </h2>
        <p className="text-gray-500 text-sm">
          Please read and accept our terms before submitting your application.
        </p>
      </div>
      {/* Terms Accordion */}
      <div className="rounded-xl border border-gray-200 overflow-hidden mb-5">
        {TERMS_SECTIONS?.map((section, idx) => (
          <div key={idx} className="border-b border-gray-100 last:border-0">
            <button
              type="button"
              onClick={() => setExpandedSection(expandedSection === idx ? null : idx)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
            >
              <span className="font-semibold text-sm" style={{ color: '#0a1628' }}>
                {section?.title}
              </span>
              <span className="text-gray-400 text-sm ml-2 flex-shrink-0">
                {expandedSection === idx ? '▲' : '▼'}
              </span>
            </button>
            {expandedSection === idx && (
              <div className="px-4 pb-4 text-xs text-gray-500 leading-relaxed border-t border-gray-100 pt-3">
                {section?.content}
              </div>
            )}
          </div>
        ))}
      </div>
      {/* Acceptance Checkboxes */}
      <div className="space-y-3 mb-6">
        {[
          {
            key: 'termsAccepted',
            label: 'I have read and agree to the OnTheGo Driver Terms & Conditions',
          },
          {
            key: 'privacyAccepted',
            label: 'I consent to the collection and processing of my personal data as described in the Privacy Policy',
          },
        ]?.map((item) => (
          <div
            key={item?.key}
            className="rounded-xl p-4 cursor-pointer transition-all duration-200"
            style={{
              background: data?.[item?.key] ? 'rgba(255,215,0,0.08)' : '#f9fafb',
              border: `2px solid ${data?.[item?.key] ? '#ffd700' : '#e5e7eb'}`,
            }}
            onClick={() => onChange(item?.key, !data?.[item?.key])}
          >
            <div className="flex items-start gap-3">
              <div
                className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 transition-all duration-200"
                style={{
                  background: data?.[item?.key]
                    ? 'linear-gradient(135deg, #ffd700, #f59e0b)'
                    : 'white',
                  border: `2px solid ${data?.[item?.key] ? '#ffd700' : '#d1d5db'}`,
                }}
              >
                {data?.[item?.key] && (
                  <span className="text-xs font-bold" style={{ color: '#0a1628' }}>
                    ✓
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{item?.label}</p>
            </div>
          </div>
        ))}
      </div>
      {/* Application Summary */}
      <div
        className="rounded-xl p-4 mb-6"
        style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}
      >
        <h3 className="font-bold text-sm mb-3" style={{ color: '#0a1628' }}>
          Application Summary
        </h3>
        <div className="space-y-1.5">
          {[
            { label: 'Personal Details', status: '✓ Complete' },
            { label: 'Vehicle Information', status: '✓ Complete' },
            { label: 'Documents Uploaded', status: '✓ Complete' },
            { label: 'ID Verification', status: '✓ Verified' },
            { label: 'Background Check', status: '✓ Consented' },
            { label: 'Insurance Plan', status: '✓ Selected' },
          ]?.map((item) => (
            <div key={item?.label} className="flex items-center justify-between">
              <span className="text-xs text-gray-500">{item?.label}</span>
              <span className="text-xs font-semibold" style={{ color: '#f59e0b' }}>
                {item?.status}
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          disabled={isSubmitting}
          className="flex-1 py-3.5 rounded-xl font-bold text-sm border-2 transition-all duration-200 hover:bg-gray-50 disabled:opacity-50"
          style={{ borderColor: '#0a1628', color: '#0a1628' }}
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={!allAccepted || isSubmitting}
          className="flex-1 py-3.5 rounded-xl font-bold text-sm transition-all duration-200 hover:opacity-90 active:scale-95 shadow-lg disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          style={{ background: 'linear-gradient(135deg, #ffd700, #f59e0b)', color: '#0a1628' }}
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-[#0a1628] border-t-transparent rounded-full animate-spin" />
              Submitting...
            </>
          ) : (
            'Submit Application 🚀'
          )}
        </button>
      </div>
    </div>
  );
};

export default TermsSubmitStep;
